import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { IconPlus } from '@tabler/icons-react';

import appSettings from '../app-settings';
import { getUserInvites, resendUserInvite, revokeUserInvite } from '../api/user-invites';
import { deleteUser } from '../api/users';
import { getCurrentUserId, getNetworkError } from '../core/utils';
import { USER_ROLES } from '../core/constants';
import { useAccount } from '../store/account/hooks';
import UserSeatsTable from '../components/user-seats/UserSeatsTable';
import { getUserEmail, getUserId } from '../components/user-seats/cognito-user';
import PendingInvitesTable from '../components/user-seats/PendingInvitesTable';
import AlertMessage from '../components/alerts/AlertMessage';
import Loader from '../components/loader/Loader';
import Button from '../components/buttons/Button';
import CreateUserModal from '../components/user-seats/CreateUserModal';
import ConfirmDeleteModal from '../components/modals/ConfirmDeleteModal';

const USERS_URL = `${appSettings.baseUrl}/accounts/users`;

const UserSeats = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [busyInviteId, setBusyInviteId] = useState(null);
  const [inviteToRevoke, setInviteToRevoke] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);

  // The route is already administrator-only; the API enforces it as well.
  const { account } = useAccount();
  const canDeleteUsers = account?.userRole === USER_ROLES.administrator;
  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const getUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(USERS_URL);
      setUsers(response.data);
    } catch (err) {
      Sentry.captureException(err);
      setError(err?.message || true);
    }
    setLoading(false);
  }, []);

  const getInvites = useCallback(async () => {
    try {
      const data = await getUserInvites();
      // Soonest-expiring first, so links about to lapse are seen.
      setInvites([...data].sort((a, b) => a.expiresAt - b.expiresAt));
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
  }, []);

  const handleCreateUserClose = useCallback(() => {
    setCreateUserOpen(false);
  }, []);

  // The modal stays open on its success step, so only refresh the list here.
  const handleInviteCreated = useCallback(() => {
    getInvites();
  }, [getInvites]);

  const handleResend = useCallback(
    async (invite) => {
      setBusyInviteId(invite.inviteId);
      try {
        const refreshed = await resendUserInvite(invite.inviteId);
        setInvites((current) => current.map((item) => (item.inviteId === invite.inviteId ? refreshed : item)));
        setSuccess(`Invitation re-sent to ${invite.email}.`);
      } catch (err) {
        Sentry.captureException(err);
        setError(getNetworkError(err));
        // A 404/409 means the invite is no longer pending; resync.
        getInvites();
      }
      setBusyInviteId(null);
    },
    [getInvites],
  );

  const handleRevoke = useCallback(async () => {
    const invite = inviteToRevoke;
    if (!invite) return;
    setBusyInviteId(invite.inviteId);
    try {
      await revokeUserInvite(invite.inviteId);
      setInvites((current) => current.filter((item) => item.inviteId !== invite.inviteId));
      setSuccess(`Invitation for ${invite.email} revoked.`);
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
      getInvites();
    }
    setBusyInviteId(null);
    setInviteToRevoke(null);
  }, [getInvites, inviteToRevoke]);

  const handleDeleteUser = useCallback(async () => {
    const user = userToDelete;
    if (!user) return;
    const userId = getUserId(user);
    setBusyUserId(userId);
    try {
      await deleteUser(userId);
      setUsers((current) => current.filter((item) => getUserId(item) !== userId));
      setSuccess(`${getUserEmail(user)} was deleted.`);
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
      // A 404 means the user is already gone; resync.
      getUsers();
    }
    setBusyUserId(null);
    setUserToDelete(null);
  }, [getUsers, userToDelete]);

  useEffect(() => {
    getUsers();
    getInvites();
  }, [getUsers, getInvites]);

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="mb-auto text-h3 dark:text-white-100">User Seats</h1>

        <Button title="Invite User" color="primary" LeftIcon={IconPlus} onClick={() => setCreateUserOpen(true)} />
      </div>

      <div className="relative flex-1 overflow-x-auto overflow-y-hidden">
        <UserSeatsTable
          data={users}
          canDelete={canDeleteUsers}
          currentUserId={currentUserId}
          busyUserId={busyUserId}
          onDelete={setUserToDelete}
        />
        {loading && <Loader />}
      </div>

      <PendingInvitesTable
        data={invites}
        busyInviteId={busyInviteId}
        onResend={handleResend}
        onRevoke={(invite) => setInviteToRevoke(invite)}
      />

      <CreateUserModal
        open={createUserOpen}
        onClose={handleCreateUserClose}
        onError={(message) => setError(message)}
        onConfirm={handleInviteCreated}
      />

      <ConfirmDeleteModal
        data={inviteToRevoke ? { id: inviteToRevoke.inviteId, name: inviteToRevoke.email, entity: 'invite' } : null}
        onClose={() => setInviteToRevoke(null)}
        onDelete={handleRevoke}
        loading={!!inviteToRevoke && busyInviteId === inviteToRevoke.inviteId}
      />

      <ConfirmDeleteModal
        data={userToDelete ? { id: getUserId(userToDelete), name: getUserEmail(userToDelete), entity: 'user' } : null}
        onClose={() => setUserToDelete(null)}
        onDelete={handleDeleteUser}
        loading={!!userToDelete && busyUserId === getUserId(userToDelete)}
        description="They will lose access to this account. This can't be undone; to give them access again, send a new invitation."
      />

      <AlertMessage
        open={!!success}
        message={success || 'Operation completed successfully!'}
        onClose={() => setSuccess('')}
        severity="success"
      />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </div>
  );
};

export default UserSeats;
